import { useState, useEffect, useRef } from 'react';

/**
 * Hook for lazy rendering components based on viewport visibility.
 * Uses IntersectionObserver to detect when an element enters the viewport.
 * 
 * @param options - IntersectionObserver options
 * @param options.triggerOnce - If true, component stays visible once it enters viewport (default: true)
 * @param options.rootMargin - Margin around root for early trigger (default: '200px')
 * @returns { ref, isVisible } - Ref to attach to element and visibility state
 */
export function useLazyRender(options?: {
  triggerOnce?: boolean;
  rootMargin?: string;
  threshold?: number;
}) {
  const { triggerOnce = true, rootMargin = '200px', threshold = 0 } = options || {};
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // If already visible and triggerOnce, no need to observe
    if (isVisible && triggerOnce) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.disconnect();
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        rootMargin,
        threshold,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [isVisible, triggerOnce, rootMargin, threshold]);

  return { ref, isVisible };
}

export default useLazyRender;
